import { useCallback, useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  type DashboardSummary,
  getGetDashboardSummaryQueryKey,
} from "@workspace/api-client-react";
import { apiErrorRateLimit } from "./apiError";

export const AI_LIMIT_WARN_THRESHOLD = 5;

export function formatResetClock(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "soon";
  return d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}

export function formatResetCountdown(
  iso: string,
  now: number = Date.now(),
): string {
  const target = new Date(iso).getTime();
  if (Number.isNaN(target)) return "soon";
  const diffMs = target - now;
  if (diffMs <= 0) return "any moment";
  const totalSeconds = Math.ceil(diffMs / 1000);
  if (totalSeconds < 60) return `${totalSeconds}s`;
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  if (m < 60) return s > 0 ? `${m}m ${s}s` : `${m}m`;
  const h = Math.floor(m / 60);
  const remM = m % 60;
  return remM > 0 ? `${h}h ${remM}m` : `${h}h`;
}

interface AiRateLimitFields {
  rateLimitRemaining: number;
  rateLimitMax: number;
  rateLimitResetAt: string;
}

export function useAiRateLimitUpdater() {
  const queryClient = useQueryClient();

  const writeToCache = useCallback(
    (remaining: number, max: number, resetAt: string) => {
      const summaryKey = getGetDashboardSummaryQueryKey();
      const cached = queryClient.getQueryData<DashboardSummary>(summaryKey);
      if (cached) {
        queryClient.setQueryData<DashboardSummary>(summaryKey, {
          ...cached,
          aiRateLimitRemaining: remaining,
          aiRateLimitMax: max,
          aiRateLimitResetAt: resetAt,
        });
      } else {
        queryClient.invalidateQueries({ queryKey: summaryKey });
      }
    },
    [queryClient],
  );

  const updateFromResponse = useCallback(
    (res: AiRateLimitFields) => {
      writeToCache(
        res.rateLimitRemaining,
        res.rateLimitMax,
        res.rateLimitResetAt,
      );
    },
    [writeToCache],
  );

  const updateFromError = useCallback(
    (err: unknown) => {
      const limit = apiErrorRateLimit(err);
      if (limit) {
        writeToCache(limit.remaining, limit.max, limit.resetAt);
      }
    },
    [writeToCache],
  );

  return { updateFromResponse, updateFromError };
}

export function useNowTick(intervalMs: number = 1000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);
  return now;
}
